/**
 * Scene-side wrapper around `FrameProbe` (docs/PERFORMANCE.md). The probe samples every
 * frame; a scene only wants a summary a few times a second, plus the `data-perf-*` probes
 * that `?perf` publishes for the opt-in Playwright perf spec.
 *
 * Play and the editor both own one of these, so the cadence and the attribute names cannot
 * drift between them.
 */
import type * as Phaser from 'phaser';
import { FrameProbe } from './frameProbe.ts';
import { EMPTY_PERF_READOUT, type PerfReadout } from '../../ui/play/perfReadout.ts';
import { RENDER_FLAGS } from '../renderFlags.ts';

/** Readout refresh cadence in ms; the probe still samples every frame. */
export const PERF_SYNC_MS = 250;

/** Publish the readout as `data-perf-*` on the canvas parent. No-op unless `?perf`. */
export function publishPerfAttributes(
  parent: Element | null | undefined,
  perf: PerfReadout,
): void {
  if (!RENDER_FLAGS.perf || !parent) {
    return;
  }

  parent.setAttribute('data-perf-frames', String(perf.frames));
  parent.setAttribute('data-perf-fps', String(perf.fps));
  parent.setAttribute('data-perf-js', perf.jsMs.toFixed(1));
  parent.setAttribute('data-perf-js-p90', perf.jsP90Ms.toFixed(1));
  parent.setAttribute('data-perf-gpu', perf.gpuMs === null ? '' : perf.gpuMs.toFixed(1));
  parent.setAttribute('data-perf-tris', String(perf.triangles));
  parent.setAttribute('data-perf-draws', String(perf.drawCalls));
  parent.setAttribute('data-perf-upload', String(perf.uploadBytes));
  parent.setAttribute('data-perf-fbo', String(perf.fboBinds));
}

export class ScenePerf {
  private readonly game: Phaser.Game;
  private probe: FrameProbe | null;
  private syncedAt = 0;
  private last: PerfReadout = EMPTY_PERF_READOUT;

  constructor(game: Phaser.Game) {
    this.game = game;
    this.probe = new FrameProbe(game);
  }

  /** Last summary handed out. Safe to read every frame. */
  get readout(): PerfReadout {
    return this.last;
  }

  /**
   * Call once per frame. Returns a fresh readout when the cadence is due — which is the
   * caller's cue to push the overlay — and `null` on the frames in between, so Preact
   * never re-renders per frame.
   */
  poll(): PerfReadout | null {
    const probe = this.probe;

    if (!probe) {
      return null;
    }

    const now = this.game.loop.now;

    if (now - this.syncedAt < PERF_SYNC_MS) {
      return null;
    }

    this.syncedAt = now;
    this.last = probe.readout();
    publishPerfAttributes(this.game.canvas.parentElement, this.last);

    return this.last;
  }

  destroy(): void {
    this.probe?.destroy();
    this.probe = null;
    this.last = EMPTY_PERF_READOUT;
    this.syncedAt = 0;
  }
}
